
import { contactsService, type Activity } from '~/services/contacts-service';
import type { Contact } from '~/types/person/contacts';
import { useGlobalToast } from '~/composables/useGlobalToast';

export const useContactsTempStore = defineStore('contacts-temp', () => {
    const auth = useAuthStore();
    const personStore = usePersonStore();
    const { showSuccess, showError, showWarning } = useGlobalToast();
    const selectedContact = ref<Contact | null>(null);
    const chatMessages = ref<any[]>([]);
    const searchTerm = ref('');
    const uploads = ref<any[]>([]);
    const capturedPhoto = ref<any>(null);

    //state
    const contacts = ref<any[]>([]);
    const filteredcontacts = ref<any[]>([]);

    const tags = ref([
        { name: 'Friends', code: 'friend' },
        { name: 'Gamers', code: 'gamer' },
        { name: 'Family', code: 'family' },
        { name: 'Coworkers', code: 'coworker' },
    ]);

    async function fetchContacts() {
        if (!personStore.person) return;

        try {
            const data = await contactsService.getContacts(personStore.person.id);
            contacts.value = data;
            filteredcontacts.value = [...contacts.value];
        } catch (error: any) {
            showError("Error", error.message || 'Something went wrong while fetching contacts');
        }
    }

    async function fetchActivities() {
        try {
            const activities = await contactsService.getActivities(10000);
            
            //only show values that have type: chat_message
            const filteredChat = activities.filter((item: Activity) => item.type === 'chat_message');

            //sort the chat messages by created_at
            filteredChat.sort((a: Activity, b: Activity) => {
                return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            });

            chatMessages.value = filteredChat;
        } catch (error: any) {
            showError("Error", error.message || 'Failed to fetch activities');
        }
    }

    async function sendMessage(message: object) {
        chatMessages.value.push(message);
    }

    const saveProfile = () => {
        showSuccess("Success", 'Profile saved successfully');
    };

    const createGroupChat = () => {
        showSuccess("Success", 'Group conversation created successfully');
    };

    const blockContact = () => {
        showSuccess("Success", 'Contact blocked successfully');
        selectedContact.value = null;
    };

    const unBlockContact = () => {
        showSuccess("Success", 'Contact unblocked successfully');
        selectedContact.value = null;
    };

    const removeContact = () => {
        showSuccess("Success", 'Contact removed successfully');
        selectedContact.value = null;
    };

    const filterContacts = (searchTerm: string) => {
        if (!searchTerm.trim()) {
            filteredcontacts.value = [...contacts.value];
        } else {
            const lowerCaseTerm = searchTerm.toLowerCase();
            filteredcontacts.value = contacts.value.filter((contact) =>
                contact.name.toLowerCase().includes(lowerCaseTerm)
            );
        }
    };

    watch(searchTerm, () => {
        setTimeout(() => {
            filterContacts(searchTerm.value);
        }, 300);
    });

    return {
        contacts,
        selectedContact,
        chatMessages,
        fetchContacts,
        fetchActivities,
        sendMessage,
        tags,
        saveProfile,
        blockContact,
        removeContact,
        filterContacts,
        searchTerm,
        createGroupChat,
        uploads,
        capturedPhoto,
        filteredcontacts,
        unBlockContact,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useContactsTempStore, import.meta.hot))
}